import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import Sidebar from "../../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  BookOpen,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  FileText,
  Video,
  ArrowRight,
  FolderOpen,
} from "lucide-react";

import { API } from "../../utils/api";

const AdminModules = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [modules, setModules] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [formData, setFormData] = useState({
    number: "",
    title: "",
    description: "",
    video_url: "",
    program_ids: [],
  });

  useEffect(() => {
    fetchModules();
    fetchPrograms();
  }, [token]);

  const fetchModules = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/modules`, {
        headers,
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setModules(data);
      }
    } catch (error) {
      console.error("Error fetching modules:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/programs`, {
        headers,
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setPrograms(data);
      }
    } catch (error) {
      console.error("Error fetching programs:", error);
    }
  };

  const handleProgramToggle = (programId) => {
    setFormData(prev => {
      const currentIds = prev.program_ids || [];
      if (currentIds.includes(programId)) {
        return { ...prev, program_ids: currentIds.filter(id => id !== programId) };
      } else {
        return { ...prev, program_ids: [...currentIds, programId] };
      }
    });
  };

  const handleCreateModule = async (e) => {
    e.preventDefault();
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/admin/modules`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          number: parseInt(formData.number),
          title: formData.title,
          description: formData.description,
          video_url: formData.video_url || null,
          program_ids: formData.program_ids,
        }),
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Module créé");
        setIsCreateOpen(false);
        setFormData({ number: "", title: "", description: "", video_url: "", program_ids: [] });
        fetchModules();
        fetchPrograms(); // Refresh program counts
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la création");
      }
    } catch (error) {
      toast.error("Erreur lors de la création");
    }
  };

  const handleUpdateModule = async (e) => {
    e.preventDefault();
    if (!selectedModule) return;

    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/admin/modules/${selectedModule.module_id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          video_url: formData.video_url || null,
          program_ids: formData.program_ids,
        }),
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Module mis à jour");
        setIsEditOpen(false);
        setSelectedModule(null);
        fetchModules();
        fetchPrograms(); // Refresh program counts
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la mise à jour");
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!confirm("Supprimer ce module ? Cette action est irréversible.")) return;

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/modules/${moduleId}`, {
        method: "DELETE",
        headers,
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Module supprimé");
        fetchModules();
        fetchPrograms();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const openEditDialog = (module) => {
    setSelectedModule(module);
    // Get program_ids from module, supporting both old (program_id) and new (program_ids) formats
    let programIds = module.program_ids || [];
    if (programIds.length === 0 && module.program_id) {
      programIds = [module.program_id];
    }
    setFormData({
      number: module.number.toString(),
      title: module.title,
      description: module.description,
      video_url: module.video_url || "",
      program_ids: programIds,
    });
    setIsEditOpen(true);
  };

  // Get program names for a module
  const getModulePrograms = (module) => {
    const ids = module.program_ids || (module.program_id ? [module.program_id] : []);
    return programs.filter(p => ids.includes(p.program_id));
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center ml-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#101E4D]"></div>
        </div>
      </div>
    );
  }

  const ModuleForm = ({ onSubmit, submitLabel, isEdit = false }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Numéro du module</Label>
          <Input
            type="number"
            value={formData.number}
            onChange={(e) => setFormData({ ...formData, number: e.target.value })}
            placeholder="1"
            required
            disabled={isEdit}
            data-testid="module-number-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Titre</Label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Titre du module"
            required
            data-testid="module-title-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Description du module..."
          rows={3}
          required
        />
      </div>

      <div className="space-y-2">
        <Label>URL Vidéo (Loom)</Label>
        <Input
          value={formData.video_url}
          onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
          placeholder="https://www.loom.com/share/..."
        />
      </div>

      {/* Programme Selection */}
      <div className="space-y-3">
        <Label>Programmes associés</Label>
        <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
          {programs.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-2">
              Aucun programme créé. Créez d'abord un programme.
            </p>
          ) : (
            programs.map((program) => (
              <div
                key={program.program_id}
                className="flex items-center space-x-3 p-2 rounded hover:bg-slate-50 cursor-pointer"
                onClick={() => handleProgramToggle(program.program_id)}
              >
                <Checkbox
                  checked={formData.program_ids?.includes(program.program_id)}
                  onCheckedChange={() => handleProgramToggle(program.program_id)}
                  data-testid={`program-checkbox-${program.program_id}`}
                />
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: program.color || '#FF6B35' }}
                />
                <span className="text-sm font-medium">{program.name}</span>
                <span className="text-xs text-slate-400">
                  ({program.user_count || 0} utilisateurs)
                </span>
              </div>
            ))
          )}
        </div>
        <p className="text-xs text-slate-500">
          Sélectionnez les programmes auxquels ce module appartient
        </p>
      </div>

      <Button
        type="submit"
        className="w-full bg-[#FF6B35] hover:bg-[#E85D2A]"
        data-testid="module-submit-btn"
      >
        {submitLabel}
      </Button>
    </form>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />

      <main className="flex-1 p-6 lg:p-8 ml-64" data-testid="admin-modules">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#101E4D] mb-2">
              Gestion des modules
            </h1>
            <p className="text-slate-600">{modules.length} modules configurés</p>
          </div>

          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#FF6B35] hover:bg-[#E85D2A]"
                onClick={() => {
                  setFormData({ number: "", title: "", description: "", video_url: "", program_ids: [] });
                  setIsCreateOpen(true);
                }}
                data-testid="create-module-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouveau module
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Créer un module</DialogTitle>
                <DialogDescription>
                  Ajoutez un nouveau module au programme de coaching
                </DialogDescription>
              </DialogHeader>
              <ModuleForm onSubmit={handleCreateModule} submitLabel="Créer le module" />
            </DialogContent>
          </Dialog>
        </div>

        {/* Modules List */}
        {modules.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="text-center py-12">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">Aucun module configuré</p>
              <Button
                onClick={() => setIsCreateOpen(true)}
                className="bg-[#FF6B35] hover:bg-[#E85D2A]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Créer le premier module
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module) => {
              const modulePrograms = getModulePrograms(module);
              return (
                <Card
                  key={module.module_id}
                  className="bg-white hover:shadow-lg transition-shadow"
                  data-testid={`module-card-${module.module_id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#101E4D] flex items-center justify-center text-white font-bold">
                          {module.number}
                        </div>
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-base line-clamp-1">{module.title}</CardTitle>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(module)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => navigate(`/admin/modules/${module.module_id}/content`)}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Gérer le contenu
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleDeleteModule(module.module_id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                      {module.description}
                    </p>

                    {/* Programme badges */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {modulePrograms.length > 0 ? (
                        modulePrograms.map((prog) => (
                          <span
                            key={prog.program_id}
                            className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded"
                            style={{
                              backgroundColor: `${prog.color || '#FF6B35'}20`,
                              color: prog.color || '#FF6B35'
                            }}
                          >
                            <FolderOpen className="w-3 h-3 mr-1" />
                            {prog.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-slate-400">Aucun programme</span>
                      )}
                    </div>

                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <div className="flex items-center gap-1">
                        <Video className="w-4 h-4" />
                        <span>{module.video_url ? "Vidéo" : "Pas de vidéo"}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        <span>{module.content_blocks?.length || 0} blocs</span>
                      </div>
                    </div>

                    <Button
                      variant="ghost"
                      className="w-full mt-4 text-[#FF6B35] hover:text-[#E85D2A] hover:bg-[#FF6B35]/5"
                      onClick={() => navigate(`/admin/modules/${module.module_id}/content`)}
                    >
                      Gérer le contenu
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifier le module</DialogTitle>
            </DialogHeader>
            <ModuleForm onSubmit={handleUpdateModule} submitLabel="Enregistrer" isEdit />
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminModules;
